import { useEffect, useRef, useState } from "react";
import { TypeAnswer } from "../components/interview/TypeAnswer";
import { SurveyNav } from "../components/survey/SurveyNav";
import { MicButton } from "../components/survey/MicButton";
import { ThinkingDots } from "../components/survey/ThinkingDots";
import { VoiceArc } from "../components/survey/VoiceArc";
import {
  OPEN_QUESTIONS,
  INITIATIVES,
  MEDAL_OPTIONS,
  ROLE_BANDS,
  LOCATIONS,
  type Medal,
  type Involvement,
} from "../lib/survey-config";
import {
  startSurvey,
  saveAnswer,
  nextQuestion,
  finalizeSurvey,
  type InitiativeSubmission,
  type ThreadTurn,
} from "../lib/survey-api";
import { fetchAggregate } from "../lib/survey-aggregate-api";

type Stage = "intro" | "identity" | "involvement" | "open" | "ranking" | "submitting" | "done";

type Custom = { id: string; label: string };
type Rating = { medal: Medal | null; discuss: boolean };

// Client-side runaway backstop only. No real cap: the person ends the
// conversation themselves with "I'm done".
const MAX_OPEN = 100;

// Each medal gets its own colour so the ranking feels like awarding something,
// not filling a form. `deep` = selected fill (cream text reads on it); `disc` =
// the little colour dot shown when unselected.
const MEDAL_STYLE: Record<string, { deep: string; disc: string }> = {
  gold: { deep: "#9c7a1e", disc: "#d4af37" },
  silver: { deep: "#6b7280", disc: "#aeb4be" },
  bronze: { deep: "#8a5424", disc: "#c0824a" },
  didnt_land: { deep: "#6f6a60", disc: "#a59f93" },
};

export default function CeoSurvey() {
  const [stage, setStage] = useState<Stage>("intro");

  // "Contribute more": a returning contributor adding fresh input. Skips identity,
  // involvement, and ranking and drops straight into a fresh director conversation.
  const [moreMode] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).has("more");
    } catch {
      return false;
    }
  });

  // Identity (anonymous — no name).
  const [location, setLocation] = useState("");
  const [dept, setDept] = useState("");
  const [roleBand, setRoleBand] = useState("");

  const responseIdRef = useRef<string | null>(null);
  const orderRef = useRef(0);

  // Involvement: per-initiative status. Absent = "not_aware" (the default).
  const [involvement, setInvolvement] = useState<Record<string, Involvement>>({});
  const [customs, setCustoms] = useState<Custom[]>([]);

  // Open-question (director) flow.
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const threadRef = useRef<ThreadTurn[]>([]);

  // Ranking (only for involved projects + customs).
  const [ratings, setRatings] = useState<Record<string, Rating>>({});

  // Guard against accidentally ending the conversation.
  const [confirmEnd, setConfirmEnd] = useState(false);
  const answeredCount = threadRef.current.length;

  // Live count for the landing arc: people who have JOINED (started), so the
  // page never feels empty while folks are mid-conversation. (Our Voices shows
  // completed contributions separately.)
  const [joinedCount, setJoinedCount] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchAggregate().then((r) => {
      if (alive && r.ok && r.stats) setJoinedCount(r.stats.total);
    });
    return () => {
      alive = false;
    };
  }, []);

  // "Share another perspective" — reset everything to a fresh, anonymous run.
  function shareAgain() {
    setLocation("");
    setDept("");
    setRoleBand("");
    setInvolvement({});
    setCustoms([]);
    setCurrentQuestion(null);
    setDraft("");
    setSaving(false);
    setRatings({});
    setConfirmEnd(false);
    responseIdRef.current = null;
    orderRef.current = 0;
    threadRef.current = [];
    setStage("intro");
  }

  // Move focus to the new stage's heading on change (keyboard + screen-reader
  // users land where the action is). The open stage is skipped: its textarea
  // autofocuses instead.
  useEffect(() => {
    if (stage === "open" || stage === "submitting") return;
    const t = window.setTimeout(() => {
      document.querySelector<HTMLElement>("[data-stage-heading]")?.focus();
    }, 60);
    return () => window.clearTimeout(t);
  }, [stage]);

  // Escape closes the confirm-end dialog.
  useEffect(() => {
    if (!confirmEnd) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConfirmEnd(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmEnd]);

  // --- Derived ---
  const statusOf = (key: string): Involvement => involvement[key] ?? "not_aware";
  // Custom projects are things they named because they worked on them → involved.
  const involvedLabels = [
    ...INITIATIVES.filter((i) => statusOf(i.key) === "directly_involved").map((i) => i.label),
    ...customs.filter((c) => c.label.trim()).map((c) => c.label.trim()),
  ];
  const awareLabels = INITIATIVES.filter((i) => statusOf(i.key) === "aware").map((i) => i.label);
  // Anything they are involved in OR aware of gets a medal. Unaware ones do not.
  const ratedList: { key: string; label: string }[] = [
    ...INITIATIVES.filter((i) => statusOf(i.key) === "directly_involved" || statusOf(i.key) === "aware").map((i) => ({ key: i.key, label: i.label })),
    ...customs.filter((c) => c.label.trim()).map((c) => ({ key: `custom:${c.id}`, label: c.label.trim() })),
  ];
  // Projects the person flagged "I have honest thoughts on this" — the director
  // makes sure the conversation gets into each of them.
  const discussLabels = ratedList.filter((it) => ratings[it.key]?.discuss).map((it) => it.label);

  // --- Start session ---
  const startAndGo = async () => {
    setStage("involvement");
    const result = await startSurvey({
      respondentLocation: location || undefined,
      respondentDept: dept || undefined,
      roleBand: roleBand || undefined,
    });
    if (result.ok && result.responseId) responseIdRef.current = result.responseId;
  };

  // "Contribute more": no identity, no involvement, no ranking. Start an anonymous
  // response and go straight into a fresh, repeat-avoiding conversation.
  const startMore = async () => {
    setStage("open");
    setCurrentQuestion(null);
    const result = await startSurvey({});
    if (result.ok && result.responseId) responseIdRef.current = result.responseId;
    await beginOpen();
  };

  // --- Involvement ---
  const setStatus = (key: string, value: Involvement) =>
    setInvolvement((prev) => ({ ...prev, [key]: value }));

  // Leaving involvement: rank the involved/aware set first (if any), else go
  // straight to the conversation. The conversation always happens.
  const continueFromInvolvement = () => {
    if (ratedList.length > 0) setStage("ranking");
    else void beginOpen();
  };

  const addCustom = () =>
    setCustoms((prev) => [...prev, { id: `c${prev.length}_${prev.length + 1}`, label: "" }]);
  const updateCustom = (id: string, label: string) =>
    setCustoms((prev) => prev.map((c) => (c.id === id ? { ...c, label } : c)));
  const removeCustom = (id: string) => setCustoms((prev) => prev.filter((c) => c.id !== id));

  // --- Director loop ---
  const staticNext = (): { question: string | null; done: boolean } => {
    const fb = OPEN_QUESTIONS[threadRef.current.length];
    return fb ? { question: fb.text, done: false } : { question: null, done: true };
  };

  const fetchNext = async (): Promise<{ question: string | null; done: boolean }> => {
    if (threadRef.current.length >= MAX_OPEN) return { question: null, done: true };
    const responseId = responseIdRef.current;
    if (responseId) {
      const r = await nextQuestion({
        responseId,
        role: roleBand || undefined,
        involvedLabels,
        awareLabels,
        discussLabels,
        thread: threadRef.current,
        mode: moreMode ? "more" : "survey",
      });
      // Daily AI budget hit or a transient AI error: keep the survey going on
      // built-in questions instead of dead-ending.
      if (r.budgetExceeded || r.degraded) return staticNext();
      return r;
    }
    // Fallback (no session yet / offline): static openers.
    return staticNext();
  };

  // Ranking now happens before the conversation, so finishing the conversation
  // goes straight to submit.
  const finishOpen = () => {
    void submitAll();
  };

  const beginOpen = async () => {
    threadRef.current = [];
    setCurrentQuestion(null);
    setDraft("");
    setStage("open");
    const r = await fetchNext();
    if (r.question) setCurrentQuestion(r.question);
    else finishOpen();
  };

  const submitOpenAnswer = async (text: string) => {
    const q = currentQuestion ?? "";
    const n = orderRef.current++;
    setSaving(true);
    setCurrentQuestion(null); // show "thinking" while the director considers
    threadRef.current = [...threadRef.current, { question: q, answer: text }];
    const responseId = responseIdRef.current;

    const [, r] = await Promise.all([
      responseId
        ? saveAnswer({ responseId, questionKey: `open_${n}`, questionText: q, answerText: text, orderIndex: n })
        : Promise.resolve({ ok: false }),
      fetchNext(),
    ]);

    setSaving(false);
    setDraft("");
    if (r.question) setCurrentQuestion(r.question);
    else finishOpen();
  };

  const skipOpen = async () => {
    if (saving) return;
    const q = currentQuestion ?? "";
    setSaving(true);
    setCurrentQuestion(null);
    threadRef.current = [...threadRef.current, { question: q, answer: "(skipped)" }];
    const r = await fetchNext();
    setSaving(false);
    setDraft("");
    if (r.question) setCurrentQuestion(r.question);
    else finishOpen();
  };

  // --- Ranking ---
  const setMedal = (key: string, medal: Medal) =>
    setRatings((prev) => ({ ...prev, [key]: { medal: prev[key]?.medal === medal ? null : medal, discuss: prev[key]?.discuss ?? false } }));
  const toggleDiscuss = (key: string) =>
    setRatings((prev) => ({ ...prev, [key]: { medal: prev[key]?.medal ?? null, discuss: !prev[key]?.discuss } }));

  const submitAll = async () => {
    setStage("submitting");
    const responseId = responseIdRef.current;

    const seeded: InitiativeSubmission[] = INITIATIVES.map((i, idx) => {
      const status = statusOf(i.key);
      const rated = status === "directly_involved" || status === "aware";
      return {
        initiativeKey: i.key,
        label: i.label,
        isCustom: false,
        involvement: status,
        medal: rated ? ratings[i.key]?.medal ?? null : null,
        followupText: null,
        orderIndex: idx,
      };
    });

    const customSubs: InitiativeSubmission[] = customs
      .filter((c) => c.label.trim())
      .map((c, idx) => ({
        initiativeKey: `custom:${c.id}`,
        label: c.label.trim(),
        isCustom: true,
        involvement: "directly_involved",
        medal: ratings[`custom:${c.id}`]?.medal ?? null,
        followupText: null,
        orderIndex: INITIATIVES.length + idx,
      }));

    // "Contribute more" runs never touch involvement/ranking, so they carry NO
    // initiative rows (writing 15 not_aware rows would falsely move the leaderboard).
    if (responseId)
      await finalizeSurvey({ responseId, initiatives: moreMode ? [] : [...seeded, ...customSubs] });
    setStage("done");
  };

  // ===================== Renders =====================

  if (stage === "intro") {
    if (moreMode) {
      return (
        <Centered animKey="more-intro">
          <div className="max-w-xl text-center space-y-7">
            <p className="text-[11px] uppercase tracking-[0.24em] text-ink-300">Nellis Auction · Add to the conversation</p>
            <h1
              data-stage-heading
              tabIndex={-1}
              className="font-display text-5xl sm:text-6xl tracking-tight text-ink-900 leading-[1.05] focus:outline-none"
            >
              Got more to add?
            </h1>
            <p className="text-ink-700 leading-relaxed text-lg">
              No forms this time. Just a quick, anonymous conversation about whatever is on your mind
              now, and we will steer toward ground the room has not covered yet.
            </p>
            <p className="text-ink-500 leading-relaxed">Anonymous. Speak freely.</p>
            <div className="py-2">
              <VoiceArc count={joinedCount} />
            </div>
            <button onClick={startMore} className={primaryBtn}>
              Start
            </button>
            <div>
              <a href="/ask" className="text-xs uppercase tracking-[0.16em] text-ink-300 hover:text-ink-500">
                back to our voices
              </a>
            </div>
          </div>
        </Centered>
      );
    }
    return (
      <Centered animKey="intro">
        <div className="max-w-xl text-center space-y-7">
          <p className="text-[11px] uppercase tracking-[0.24em] text-ink-300">Nellis Auction · A living survey</p>
          <h1
            data-stage-heading
            tabIndex={-1}
            className="font-display text-5xl sm:text-6xl tracking-tight text-ink-900 leading-[1.05] focus:outline-none"
          >
            Leadership Input, 2026 so far
          </h1>
          <p className="text-ink-700 leading-relaxed text-lg">
            A short, honest conversation about how the year is really going, shaped around your role.
          </p>
          <p className="text-ink-500 leading-relaxed">
            Anonymous. Honesty and openness valued. Speak freely.
          </p>
          <div className="py-2">
            <VoiceArc count={joinedCount} />
          </div>
          <button onClick={() => setStage("identity")} className={primaryBtn}>
            Begin
          </button>
          <div>
            <a href="/ask" className="text-xs uppercase tracking-[0.16em] text-ink-300 hover:text-ink-500">
              or hear our voices
            </a>
          </div>
        </div>
      </Centered>
    );
  }

  if (stage === "identity") {
    return (
      <Chrome animKey="identity">
        <div className="w-full max-w-lg mx-auto space-y-8 py-6">
          <div className="text-center space-y-3">
            <h2 data-stage-heading tabIndex={-1} className="font-display text-3xl text-ink-900 focus:outline-none">
              A little about you
            </h2>
            <p className="text-ink-500 leading-relaxed">
              Anonymous, we never collect your name. This just shapes the questions around your role.
            </p>
          </div>
          <div className="space-y-5">
            <Field label="Role">
              <select value={roleBand} onChange={(e) => setRoleBand(e.target.value)} className={inputCls}>
                <option value="">Optional</option>
                {ROLE_BANDS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </Field>
            <Field label="Building / Location">
              <select value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls}>
                <option value="">Optional</option>
                {LOCATIONS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </Field>
            <Field label="Department">
              <input value={dept} onChange={(e) => setDept(e.target.value)} className={inputCls} placeholder="Optional" />
            </Field>
          </div>
          <div className="flex items-center justify-between pt-2">
            <button onClick={startAndGo} className="text-sm text-ink-300 hover:text-ink-500 transition-colors">
              Skip this
            </button>
            <button onClick={startAndGo} className={primaryBtn}>Continue</button>
          </div>
        </div>
      </Chrome>
    );
  }

  if (stage === "involvement") {
    return (
      <Chrome animKey="involvement">
        <div className="max-w-2xl mx-auto py-4">
          <div className="text-center space-y-3 mb-8">
            <h2 data-stage-heading tabIndex={-1} className="font-display text-3xl sm:text-4xl text-ink-900 focus:outline-none">
              Where do these sit for you?
            </h2>
            <p className="text-ink-500 leading-relaxed max-w-lg mx-auto">
              These are 2026's projects. Mark each one. Everything starts as Unaware, just flag what
              you were Involved in or Aware of. We tailor the conversation and only ask you to rate
              the ones you know.
            </p>
          </div>
          <div className="space-y-2">
            {INITIATIVES.map((i) => (
              <div
                key={i.key}
                className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-parchment-300 bg-parchment-50 px-4 py-3"
              >
                <span className="min-w-0">
                  <span className="font-display text-base text-ink-900">{i.label}</span>
                  <span className="text-[11px] uppercase tracking-[0.14em] text-ink-300 ml-2">{i.owner}</span>
                </span>
                <StatusToggle value={statusOf(i.key)} onChange={(v) => setStatus(i.key, v)} />
              </div>
            ))}
            {customs.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-xl border border-dashed border-ink-300/50 px-4 py-2">
                <span className="text-[10px] uppercase tracking-[0.14em] text-ink-300 shrink-0">Involved</span>
                <input
                  value={c.label}
                  onChange={(e) => updateCustom(c.id, e.target.value)}
                  placeholder="Name a project you worked on"
                  className="flex-1 bg-transparent py-1 text-ink-900 placeholder:text-ink-300/80 focus:outline-none"
                />
                <button onClick={() => removeCustom(c.id)} className="text-xs text-ink-300 hover:text-ink-500">Remove</button>
              </div>
            ))}
          </div>
          <button onClick={addCustom} className="mt-3 text-sm text-accent-600 hover:underline underline-offset-4">
            + Add one we missed
          </button>
          <div className="mt-10 flex justify-center">
            <button onClick={continueFromInvolvement} className={primaryBtn}>Continue</button>
          </div>
        </div>
      </Chrome>
    );
  }

  if (stage === "open") {
    return (
      <Chrome animKey="open">
        <section aria-live="polite" className="flex-1 flex flex-col max-w-3xl w-full mx-auto py-6 sm:py-12">
          {/* my-auto centers a short question, but lets a long one grow and the page
              scroll instead of clipping (the input always stays reachable below it). */}
          <div className="my-auto w-full flex flex-col items-center">
          {currentQuestion === null ? (
            <ThinkingDots label="Thinking about what to ask you" />
          ) : (
            <>
              <h2
                key={currentQuestion}
                className="font-display text-3xl sm:text-4xl text-ink-900 text-center leading-[1.2] max-w-2xl soft-in"
              >
                {currentQuestion}
              </h2>
              <div className="mt-10 w-full flex flex-col items-center gap-4">
                <TypeAnswer
                  key={threadRef.current.length}
                  value={draft}
                  onValueChange={setDraft}
                  onSubmit={submitOpenAnswer}
                  disabled={saving}
                  placeholder="Speak or type, whatever's easier…"
                  autoFocus
                />
                <MicButton disabled={saving} value={draft} onChange={setDraft} />
                <p className="text-xs text-ink-300 text-center max-w-sm leading-relaxed">
                  The mic is faster than typing. Just talk, ums and pauses are fine.
                </p>
                <div className="flex items-center gap-6">
                  <button onClick={skipOpen} disabled={saving} className="text-xs text-ink-300 hover:text-ink-500 transition-colors">
                    {saving ? "Thinking…" : "Skip this one"}
                  </button>
                  {/* Only after a few questions, and behind a confirm, so it is never
                      mistaken for "next". */}
                  {answeredCount >= 4 && (
                    <button
                      onClick={() => setConfirmEnd(true)}
                      disabled={saving}
                      className="text-xs uppercase tracking-[0.16em] text-ink-500 hover:text-ink-900 transition-colors"
                    >
                      Wrap up & finish
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
          </div>
        </section>

        {confirmEnd && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/30 px-6 fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-end-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmEnd(false);
            }}
          >
            <div className="max-w-sm w-full rounded-2xl bg-parchment-50 border border-parchment-300 p-7 text-center shadow-[0_24px_70px_-24px_oklch(22%_0.02_60/0.45)] modal-in">
              <h3 id="confirm-end-title" className="font-display text-2xl text-ink-900 mb-2">
                Wrap up the conversation?
              </h3>
              <p className="text-sm text-ink-500 leading-relaxed mb-6">
                This ends the questions and wraps up your input. If you have more to say, keep going.
                There is no rush.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => {
                    setConfirmEnd(false);
                    finishOpen();
                  }}
                  className={primaryBtn}
                >
                  Yes, I'm done
                </button>
                <button
                  autoFocus
                  onClick={() => setConfirmEnd(false)}
                  className="text-sm text-ink-500 hover:text-ink-900 py-2 transition-colors"
                >
                  Keep going
                </button>
              </div>
            </div>
          </div>
        )}
      </Chrome>
    );
  }

  if (stage === "ranking") {
    return (
      <Chrome animKey="ranking">
        <div className="max-w-2xl mx-auto py-4">
          <div className="text-center space-y-3 mb-8">
            <h2 data-stage-heading tabIndex={-1} className="font-display text-3xl sm:text-4xl text-ink-900 focus:outline-none">
              How did they land?
            </h2>
            <p className="text-ink-500 leading-relaxed max-w-lg mx-auto">
              For the projects you were involved in or aware of, award a medal for how it landed. Tick
              any you have honest thoughts on, and we will get into those in the conversation.
            </p>
          </div>
          <div className="space-y-4">
            {ratedList.map((it) => (
              <div key={it.key} className="rounded-2xl border border-parchment-300 bg-parchment-100 p-5">
                <h3 className="font-display text-lg text-ink-900 mb-3">{it.label}</h3>
                <div className="flex flex-wrap gap-2">
                  {MEDAL_OPTIONS.map((opt) => {
                    const sel = ratings[it.key]?.medal === opt.value;
                    const st = MEDAL_STYLE[opt.value];
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setMedal(it.key, opt.value)}
                        aria-pressed={sel}
                        style={sel ? { backgroundColor: st.deep, borderColor: st.deep, color: "#f6efe2" } : undefined}
                        className={[
                          "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-all duration-200 active:scale-95",
                          sel
                            ? "scale-[1.04] shadow-[0_6px_16px_-8px_rgba(0,0,0,0.4)]"
                            : "bg-parchment-50 text-ink-700 border-parchment-300 hover:border-ink-300",
                        ].join(" ")}
                      >
                        <span
                          aria-hidden="true"
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: sel ? "#f6efe2" : st.disc }}
                        />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => toggleDiscuss(it.key)}
                  aria-pressed={!!ratings[it.key]?.discuss}
                  className="mt-4 flex items-center gap-3 text-left group"
                >
                  <span
                    aria-hidden="true"
                    className={[
                      "h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors",
                      ratings[it.key]?.discuss
                        ? "bg-ink-900 border-ink-900 text-parchment-50"
                        : "border-ink-300 bg-parchment-50 group-hover:border-ink-500",
                    ].join(" ")}
                  >
                    {ratings[it.key]?.discuss && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </span>
                  <span className="text-sm text-ink-700 leading-snug">
                    I have honest thoughts on this.{" "}
                    <span className="text-ink-300">We will get into it in the conversation.</span>
                  </span>
                </button>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <button onClick={() => void beginOpen()} className={primaryBtn}>Continue</button>
          </div>
        </div>
      </Chrome>
    );
  }

  if (stage === "submitting") {
    return (
      <Centered animKey="submitting">
        <ThinkingDots label="Adding your voice" />
      </Centered>
    );
  }

  return (
    <Centered animKey="done">
      <div className="max-w-xl text-center space-y-6">
        <h1 data-stage-heading tabIndex={-1} className="font-hand text-6xl text-ink-900 focus:outline-none">
          Thank you.
        </h1>
        <p className="text-ink-700 leading-relaxed text-lg">
          Your voice is in. It is now part of Our Voices, what the whole room can hear and ask about.
        </p>
        <div className="flex flex-col items-center gap-4 pt-2">
          <a href="/ask" className="inline-block text-sm uppercase tracking-[0.16em] text-accent-600 underline-offset-4 hover:underline">
            Hear our voices
          </a>
          <button
            onClick={shareAgain}
            className="text-xs uppercase tracking-[0.16em] text-ink-300 hover:text-ink-500 transition-colors"
          >
            Share another perspective
          </button>
        </div>
      </div>
    </Centered>
  );
}

// ===================== UI atoms =====================

function StatusToggle({ value, onChange }: { value: Involvement; onChange: (v: Involvement) => void }) {
  const opts: { value: Involvement; label: string }[] = [
    { value: "directly_involved", label: "Involved" },
    { value: "aware", label: "Aware" },
    { value: "not_aware", label: "Unaware" },
  ];
  return (
    <div className="inline-flex rounded-full border border-parchment-300 bg-parchment-100 p-0.5 shrink-0 self-start sm:self-auto">
      {opts.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={active}
            className={[
              "px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[24px]",
              active ? "bg-ink-900 text-parchment-50" : "text-ink-500 hover:text-ink-900",
            ].join(" ")}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] text-ink-300 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Centered({ children, animKey }: { children: React.ReactNode; animKey?: string }) {
  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center">
      <div key={animKey} className="stage-in w-full flex flex-col items-center">
        {children}
      </div>
    </main>
  );
}

function Chrome({ children, animKey }: { children: React.ReactNode; animKey?: string }) {
  return (
    <main className="min-h-screen flex flex-col px-6 py-6 sm:py-8">
      <SurveyNav current="survey" />
      <div key={animKey} className="flex-1 flex flex-col w-full stage-in">
        {children}
      </div>
    </main>
  );
}

const inputCls =
  "w-full rounded-xl border border-parchment-300 bg-parchment-50 px-4 py-3 text-ink-900 placeholder:text-ink-300/80 focus:outline-none focus:border-ink-300";

const primaryBtn =
  "px-8 py-3 rounded-full text-sm font-medium bg-ink-900 text-parchment-50 hover:bg-ink-700 active:scale-[0.98] transition-all duration-300 shadow-[0_4px_14px_-6px_oklch(22%_0.02_60/0.4)]";
